import { toast } from 'sonner';
import { useItemsStore } from '@/features/timeline/stores/items-store';
import { useCompositionsStore, type SubComposition } from '@/features/timeline/stores/compositions-store';
import { useTimelineSettingsStore } from '@/features/timeline/stores/timeline-settings-store';
import { useSelectionStore } from '@/features/editor/stores/selection-store';
import type { TimelineItem, TimelineTrack, CompositionItem } from '@/types/timeline';
import type { Transition } from '@/types/transition';
import type { ItemKeyframes } from '@/types/keyframe';
import { dissolvePreComp } from '@/features/timeline/stores/actions/composition-actions';

type SavedSceneLike = {
  id: string;
  name: string;
  payload?: {
    templateTimeline?: {
      timeline?: {
        tracks?: TimelineTrack[];
        items?: TimelineItem[];
        transitions?: Transition[];
        keyframes?: ItemKeyframes[];
        width?: number;
        height?: number;
        fps?: number;
        durationInFrames?: number;
      };
    };
  } | null;
};

type SceneInsertOptions = {
  placement?: 'start' | 'end' | 'after_scene';
  anchorSceneItemId?: string;
};

const cloneTrack = (track: TimelineTrack, order: number): TimelineTrack => ({
  ...track,
  id: crypto.randomUUID(),
  order,
  items: [],
});

const cloneItem = (item: TimelineItem, trackId: string): TimelineItem => {
  const newId = crypto.randomUUID();
  return {
    ...item,
    id: newId,
    trackId,
    originId: item.originId ? `${item.originId}-scene-${newId}` : `scene-${newId}`,
    transform: item.transform ? { ...item.transform } : item.transform,
  };
};

const findSceneBlockEnd = (sceneItemId: string, items: TimelineItem[]) => {
  const sceneItem = items.find((item) => item.id === sceneItemId);
  if (!sceneItem) return null;

  const sceneStart = sceneItem.from;
  const sceneEnd = sceneItem.from + sceneItem.durationInFrames;
  const overlapping = items.filter((item) => item.from < sceneEnd && item.from + item.durationInFrames > sceneStart);
  return overlapping.reduce((max, item) => Math.max(max, item.from + item.durationInFrames), sceneEnd);
};

const resolveInsertionFrame = (
  options: SceneInsertOptions,
  rootItems: TimelineItem[]
) => {
  const placement = options.placement || 'end';
  if (placement === 'start') return 0;
  if (placement === 'after_scene' && options.anchorSceneItemId) {
    const blockEnd = findSceneBlockEnd(options.anchorSceneItemId, rootItems);
    if (typeof blockEnd === 'number') return blockEnd;
  }
  return rootItems.reduce((max, item) => Math.max(max, item.from + item.durationInFrames), 0);
};

export const insertSavedSceneAsset = (savedScene: SavedSceneLike, options: SceneInsertOptions = {}) => {
  const timeline = savedScene.payload?.templateTimeline?.timeline;
  if (!timeline?.tracks?.length || !timeline?.items?.length) {
    toast.error('이 씬은 아직 superedit 레이아웃 데이터가 없습니다.');
    return false;
  }

  const itemsStore = useItemsStore.getState();
  const compositionsStore = useCompositionsStore.getState();
  const settingsStore = useTimelineSettingsStore.getState();
  const rootItems = itemsStore.items;

  const sourceTracks = timeline.tracks || [];
  const sourceItems = timeline.items || [];
  const sourceTransitions = timeline.transitions || [];
  const sourceKeyframes = timeline.keyframes || [];
  const width = timeline.width || 1920;
  const height = timeline.height || 1080;
  const fps = timeline.fps || settingsStore.fps || 30;
  const durationInFrames =
    timeline.durationInFrames ||
    sourceItems.reduce((max, item) => Math.max(max, item.from + item.durationInFrames), 0);
  const insertionFrame = resolveInsertionFrame(options, rootItems);

  const trackIdMap = new Map<string, string>();
  const clonedTracks = sourceTracks
    .sort((a, b) => a.order - b.order)
    .map((track, index) => {
      const cloned = cloneTrack(track, index);
      trackIdMap.set(track.id, cloned.id);
      return cloned;
    });

  const itemIdMap = new Map<string, string>();
  const clonedItems = sourceItems.map((item) => {
    const mappedTrackId = trackIdMap.get(item.trackId) || clonedTracks[0]?.id || item.trackId;
    const cloned = cloneItem(item, mappedTrackId);
    itemIdMap.set(item.id, cloned.id);
    return cloned;
  });

  const clonedTransitions = sourceTransitions
    .filter((transition) => itemIdMap.has(transition.leftClipId) && itemIdMap.has(transition.rightClipId))
    .map((transition) => ({
      ...transition,
      id: crypto.randomUUID(),
      leftClipId: itemIdMap.get(transition.leftClipId)!,
      rightClipId: itemIdMap.get(transition.rightClipId)!,
      trackId: trackIdMap.get(transition.trackId) || transition.trackId,
    }));

  const clonedKeyframes = sourceKeyframes
    .filter((entry) => itemIdMap.has(entry.itemId))
    .map((entry) => ({
      ...entry,
      itemId: itemIdMap.get(entry.itemId)!,
      properties: entry.properties.map((property) => ({
        ...property,
        keyframes: property.keyframes.map((keyframe) => ({
          ...keyframe,
          id: crypto.randomUUID(),
        })),
      })),
    }));

  const compositionId = crypto.randomUUID();
  const compositionName = savedScene.name || 'Saved Scene';

  const subComposition: SubComposition = {
    id: compositionId,
    name: compositionName,
    items: clonedItems,
    tracks: clonedTracks,
    transitions: clonedTransitions,
    keyframes: clonedKeyframes,
    fps,
    width,
    height,
    durationInFrames,
  };

  compositionsStore.addComposition(subComposition);

  const rootTracks = itemsStore.tracks;
  const visualTrack =
    rootTracks.find((track) => track.name.toLowerCase() === 'visuals' && !track.isGroup) ||
    rootTracks.find((track) => !track.isGroup) ||
    null;

  if (!visualTrack) {
    toast.error('삽입할 메인 트랙을 찾지 못했습니다.');
    return false;
  }

  const compositionItem: CompositionItem = {
    id: crypto.randomUUID(),
    type: 'composition',
    trackId: visualTrack.id,
    from: insertionFrame,
    durationInFrames: Math.max(1, durationInFrames),
    label: compositionName,
    compositionId,
    compositionWidth: width,
    compositionHeight: height,
    transform: {
      x: 0,
      y: 0,
      rotation: 0,
      opacity: 1,
    },
  };

  const shiftedRootItems = rootItems.map((item) => {
    if (item.from < insertionFrame) return item;
    return {
      ...item,
      from: item.from + durationInFrames,
      transform: item.transform ? { ...item.transform } : item.transform,
    };
  });

  itemsStore.setItems([...shiftedRootItems, compositionItem].sort((a, b) => a.from - b.from));
  settingsStore.markDirty();
  useSelectionStore.getState().selectItems([compositionItem.id]);
  dissolvePreComp(compositionItem.id);

  toast.success(`${savedScene.name} 씬을 현재 위치에 불러왔습니다.`);
  return true;
};
